import { KeyboardEvent, MouseEvent, ReactNode, createContext } from 'react';
import {
  TActionsMap,
  TUseProperties,
  TDistinctorProperties,
} from '@apia/dom-store';
import { TListboxState, TRowDistinctions } from './common';
import { operations } from './operations';
import { TOnConfirmSelection, TOnSelectionChange } from '.';
import { BoxProps } from '@apia/theme';

export type TListbox = {
  children: ReactNode | ReactNode[];
  className?: string;
  /**
   * Permite remover los estilos por defecto de la lista a fin de personalizar
   * su aspecto
   */
  unstyled?: boolean;
  onClickNode?: (ev: MouseEvent<HTMLElement>) => unknown;
  onDoubleClickNode?: (ev: MouseEvent<HTMLElement>) => unknown;
  /**
   * Este evento es lanzado cuando el usuario estando en foco en la lista
   * presiona:
   *
   * - Enter si la selección múltiple está deshabilitada
   * - ctrl+Enter si la selección múltiple está habilitada
   */
  onConfirmSelection?: (ev: TOnConfirmSelection) => unknown;
  onPressEnterOnNode?: (ev: KeyboardEvent<HTMLElement>) => unknown;
  onPressKeyOnNode?: (ev: KeyboardEvent<HTMLElement>) => unknown;
  onSelectionChange?: (ev: TOnSelectionChange) => unknown;
  /**
   * onKeyDown e id no son permitidas dado que se usan para generar el
   * comportamiento correcto del componente
   */
  ulProps?: Omit<BoxProps, 'id' | 'onKeyDown' | 'sx'>;
} & Partial<
  Pick<
    TListboxState,
    | 'allowMultipleCharacterSearch'
    | 'allowSelection'
    | 'allowSingleCharacterSearch'
    | 'isMultipleSelection'
    | 'searchFunction'
    | 'startsWithFunction'
    | 'showSearchbox'
  >
>;

export type TListboxContext = {
  getState: () => TListboxState;
  id: string;
  listboxActions: TActionsMap<TListboxState, typeof operations>;
  useListbox: TUseProperties<
    TListboxState,
    {
      row: [
        (
          distinctions: TRowDistinctions,
          state: TListboxState,
        ) => TDistinctorProperties,
      ];
      searchbox: [
        (distinctions: null, state: TListboxState) => TDistinctorProperties,
      ];
      listbox: [
        (distinctions: null, state: TListboxState) => TDistinctorProperties,
      ];
    }
  >;
  variant: string;
} & Pick<
  TListbox,
  | 'onClickNode'
  | 'onConfirmSelection'
  | 'onDoubleClickNode'
  | 'onPressEnterOnNode'
  | 'onPressKeyOnNode'
>;

const ListboxContext = createContext<TListboxContext>({} as TListboxContext);

export { ListboxContext };
